import type { PolicyVerdict } from "./contracts.js";
import { type ActionKind, ALWAYS_NEVER, type FloorKind, type PolicyPreset, type PolicyRule } from "./wire.js";

/** The three setup presets from the mock (screen 5). */
export const PRESETS: Record<PolicyPreset, PolicyRule[]> = {
  careful: [
    { kind: "restart", mode: "ask" },
    { kind: "add_capacity", mode: "ask" },
    { kind: "undo_update", mode: "ask" },
    { kind: "open_pr", mode: "ask" },
    { kind: "email_customers", mode: "ask" },
    { kind: "pause", mode: "ask" },
    { kind: "custom", mode: "ask" },
    { kind: "data_change", mode: "never" },
  ],
  balanced: [
    { kind: "restart", mode: "alone" },
    { kind: "add_capacity", mode: "alone", when: { maxCapacityFactor: 2 } },
    { kind: "undo_update", mode: "ask" },
    { kind: "open_pr", mode: "alone" },
    { kind: "email_customers", mode: "ask" },
    { kind: "pause", mode: "ask" },
    { kind: "custom", mode: "ask" },
    { kind: "data_change", mode: "never" },
  ],
  handsoff: [
    { kind: "restart", mode: "alone" },
    { kind: "add_capacity", mode: "alone", when: { maxCapacityFactor: 3 } },
    { kind: "undo_update", mode: "alone" },
    { kind: "open_pr", mode: "alone" },
    { kind: "email_customers", mode: "ask" },
    { kind: "pause", mode: "ask" },
    { kind: "custom", mode: "ask" },
    { kind: "data_change", mode: "never" },
  ],
};

export type PolicyContext = {
  floor: FloorKind | null;
  /** Seconds since the most recent deploy, if any. */
  sinceDeployS: number | null;
  capacityFactor?: number;
  /** Irreversible actions are never "alone". */
  reversible: boolean;
};

function matches(rule: PolicyRule, ctx: PolicyContext): boolean {
  const w = rule.when;
  if (!w) return true;
  if (w.floor && (ctx.floor === null || !w.floor.includes(ctx.floor))) return false;
  if (w.afterDeployWithinS !== undefined && (ctx.sinceDeployS === null || ctx.sinceDeployS > w.afterDeployWithinS))
    return false;
  if (w.maxCapacityFactor !== undefined && (ctx.capacityFactor ?? 1) > w.maxCapacityFactor) return false;
  return true;
}

/**
 * Pure policy evaluation. Order: hard never-list → most specific matching rule (one with `when`)
 * → general rule → default "ask". Irreversible actions are capped at "ask".
 */
export function evaluatePolicy(rules: PolicyRule[], kind: ActionKind, ctx: PolicyContext): PolicyVerdict {
  if (ALWAYS_NEVER.includes(kind)) {
    return { mode: "never", rule: null, reason: `${kind} is never allowed` };
  }
  const forKind = rules.filter((r) => r.kind === kind);
  const specific = forKind.find((r) => r.when !== undefined && matches(r, ctx));
  const general = forKind.find((r) => r.when === undefined);
  const rule = specific ?? general ?? null;
  let mode = rule?.mode ?? "ask";
  let reason = rule ? `rule ${kind}=${rule.mode}${specific ? " (specific)" : ""}` : `no rule for ${kind}; ask`;
  if (rule?.when && !matches(rule, ctx)) {
    mode = "ask";
    reason = `rule ${kind} conditions not met; ask`;
  }
  if (mode === "alone" && !ctx.reversible) {
    mode = "ask";
    reason = `${kind} is not reversible; ask`;
  }
  return { mode, rule, reason };
}
